import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity, BarChart3, Building2, CheckCircle2, ClipboardList,
  Globe, ListChecks, Mail, MessageSquare, Package, Phone, Save, ScrollText,
  Settings2, ShoppingCart, Users
} from 'lucide-react';
import {
  Alert, AsyncBoundary, Badge, Button, DataTable, Field, Input, Modal, SectionTitle,
  Select, StatCard, StatGrid, Tabs, Textarea, Timeline
} from '../ui';
import {
  ACTOR_LABELS, EVENT_LABELS, REQUEST_STATUS, SERVICE_STATUS, adminService,
  formatDate, formatDateTime, formatMoney
} from '../../services/agencyService';

// ⭐ Ficha del cliente (spec §4).
//
// Un cliente de MARKETOS puede tener a la vez una página web, un CRM, un POS y
// campañas. Meter todo en una sola pantalla sería ilegible, y separarlo en
// pantallas distintas obligaría a navegar de ida y vuelta para responder
// "¿cómo va este cliente?". Las pestañas son el punto medio: un solo contexto
// (este cliente), varias vistas.
//
// Las pestañas de Página Web / CRM / POS / Campañas muestran el estado
// derivado de los productos contratados. NO exponen configuración técnica ni
// credenciales: eso vive en el POS del propio cliente, y spec §2 lo prohíbe
// expresamente en esta vista.

const SERVICE_STATE_OPTIONS = Object.entries(SERVICE_STATUS).map(([value, meta]) => ({ value, label: meta.label }));
const WEB_PROJECT_STATUS_OPTIONS = [
  ['setup', 'Proyecto creado'],
  ['waiting_content', 'Esperando información'],
  ['ready_to_build', 'Información lista para construir'],
  ['building', 'Construyendo la página'],
  ['quality_review', 'Control de calidad'],
  ['client_review', 'Revisión del cliente'],
  ['approved', 'Diseño aprobado'],
  ['domain_setup', 'Configurando publicación'],
  ['published', 'Página publicada'],
  ['warranty', 'En garantía inicial'],
  ['complete', 'Implementación completada'],
  ['paused', 'Proyecto pausado']
];
const WEB_INTAKE_STATUS_OPTIONS = [
  ['not_started', 'Formulario aún no iniciado'],
  ['draft', 'Cliente completando información'],
  ['submitted', 'Información enviada'],
  ['needs_changes', 'Faltan correcciones'],
  ['validated', 'Información validada'],
  ['locked', 'Información cerrada para producción']
];
const WEBSITE_STAGE_OPTIONS = [
  { value: 'contracted', label: 'Contrato confirmado' },
  { value: 'collecting_content', label: 'Recibiendo información' },
  { value: 'designing', label: 'Diseñando la página' },
  { value: 'client_review', label: 'Esperando revisión del cliente' },
  { value: 'domain_setup', label: 'Configurando dominio y publicación' },
  { value: 'published', label: 'Página publicada' },
  { value: 'warranty', label: 'En garantía inicial' },
  { value: 'complete', label: 'Implementación completada' }
];
const WEBSITE_PROCESS_PHASES = [
  {
    id: 'activation',
    title: '1. Venta y activación',
    purpose: 'Confirmar exactamente qué compró el cliente y dejar el proyecto listo para iniciar.',
    tasks: [
      ['plan_confirmed', 'Modalidad, precio y alcance confirmados'],
      ['contract_confirmed', 'Términos y condiciones aceptados'],
      ['payment_confirmed', 'Pago inicial confirmado'],
      ['client_access_created', 'Acceso del cliente creado y entregado'],
      ['responsible_assigned', 'Responsable MARKETOS asignado'],
      ['start_date_scheduled', 'Fecha de inicio registrada']
    ]
  },
  {
    id: 'onboarding',
    title: '2. Onboarding y contenido',
    purpose: 'Reunir y validar todo lo necesario antes de comenzar el diseño.',
    tasks: [
      ['onboarding_message_sent', 'Mensaje de bienvenida enviado'],
      ['brief_sent', 'Formulario de información enviado'],
      ['brief_received', 'Información del negocio recibida'],
      ['business_data_verified', 'Nombre, descripción, dirección y horarios verificados'],
      ['logo_received', 'Logo recibido'],
      ['brand_style_confirmed', 'Colores y estilo visual confirmados'],
      ['copy_received', 'Textos recibidos o autorizados para redacción'],
      ['images_received_or_selected', 'Fotografías recibidas o selección autorizada'],
      ['catalog_items_received', 'Hasta 10 productos, categorías o servicios recibidos'],
      ['contact_data_confirmed', 'Teléfono, correo y WhatsApp confirmados'],
      ['social_links_confirmed', 'Enlaces de redes sociales confirmados']
    ]
  },
  {
    id: 'production',
    title: '3. Producción de la página',
    purpose: 'Construir la página completa siguiendo el alcance comercial contratado.',
    tasks: [
      ['project_created', 'Proyecto creado en el desplegador MARKETOS'],
      ['template_selected', 'Plantilla o dirección visual seleccionada'],
      ['structure_defined', 'Estructura de hasta 6 secciones definida'],
      ['copy_edited', 'Textos comerciales redactados y ordenados'],
      ['visual_design_ready', 'Diseño visual principal terminado'],
      ['catalog_items_loaded', 'Hasta 10 elementos cargados'],
      ['whatsapp_confirmed', 'Número de WhatsApp verificado'],
      ['whatsapp_ctas_configured', 'Botones estratégicos de WhatsApp configurados'],
      ['basic_metadata_configured', 'Título y descripción para buscadores configurados'],
      ['share_image_configured', 'Imagen para compartir configurada'],
      ['favicon_configured', 'Ícono del sitio configurado'],
      ['desktop_layout_checked', 'Diseño para computadora revisado'],
      ['mobile_layout_checked', 'Diseño para celular revisado'],
      ['design_ready', 'Producción marcada como terminada']
    ]
  },
  {
    id: 'quality',
    title: '4. Control de calidad interno',
    purpose: 'Detectar errores antes de que el cliente reciba la vista previa.',
    tasks: [
      ['spelling_checked', 'Ortografía y redacción revisadas'],
      ['prices_checked', 'Precios y nombres de productos revisados'],
      ['contact_data_checked', 'Datos de contacto revisados'],
      ['links_checked', 'Todos los enlaces probados'],
      ['whatsapp_tested', 'Mensajes y botones de WhatsApp probados'],
      ['images_optimized', 'Imágenes optimizadas y sin deformaciones'],
      ['cross_browser_checked', 'Página revisada en navegadores principales'],
      ['internal_qa_approved', 'Control interno aprobado por MARKETOS']
    ]
  },
  {
    id: 'review',
    title: '5. Revisión del cliente',
    purpose: 'Obtener una aprobación clara antes de conectar dominio y publicar.',
    tasks: [
      ['preview_link_ready', 'Enlace de vista previa preparado'],
      ['client_review_sent', 'Vista previa enviada al cliente'],
      ['feedback_received', 'Comentarios del cliente recibidos'],
      ['feedback_applied', 'Cambios acordados aplicados'],
      ['client_approved', 'Aprobación final registrada']
    ]
  },
  {
    id: 'publication',
    title: '6. Dominio y publicación',
    purpose: 'Poner la página en línea de forma segura y verificar el resultado final.',
    tasks: [
      ['domain_ownership_confirmed', 'Propietario y nombre del dominio confirmados'],
      ['domain_reserved_or_purchased', 'Dominio reservado, comprado o recibido'],
      ['dns_configured', 'Registros del dominio configurados'],
      ['hosting_ready', 'Hosting preparado'],
      ['domain_configured', 'Dominio personalizado conectado'],
      ['ssl_active', 'Certificado de seguridad SSL activo'],
      ['backup_created', 'Respaldo previo a publicación creado'],
      ['published', 'Página publicada']
    ]
  },
  {
    id: 'delivery',
    title: '7. Entrega, garantía y renovación',
    purpose: 'Cerrar la implementación sin perder el seguimiento posterior.',
    tasks: [
      ['publication_verified', 'Página publicada verificada nuevamente'],
      ['publication_url_sent', 'Enlace final enviado al cliente'],
      ['portal_access_confirmed', 'Cliente confirmó acceso a su portal'],
      ['warranty_started', 'Garantía inicial de 5 días laborables iniciada'],
      ['warranty_end_scheduled', 'Fin de garantía registrado'],
      ['renewal_date_scheduled', 'Renovación o siguiente cobro programado'],
      ['support_mode_recorded', 'Modalidad de soporte registrada'],
      ['warranty_closed', 'Garantía inicial cerrada']
    ]
  }
];

const WEBSITE_CHECKLIST_FIELDS = WEBSITE_PROCESS_PHASES.flatMap((phase) => phase.tasks);

const suggestedWebsiteStage = (checklist = {}) => {
  if (checklist.warranty_closed) return 'complete';
  if (checklist.published) return 'warranty';
  if (checklist.client_approved) return 'domain_setup';
  if (checklist.client_review_sent || checklist.preview_link_ready) return 'client_review';
  if (checklist.brief_received || checklist.project_created) return 'designing';
  if (checklist.contract_confirmed || checklist.payment_confirmed) return 'collecting_content';
  return 'contracted';
};

export default function FichaCliente({ clientId, open, onClose, onChanged }) {
  const [tab, setTab] = useState('resumen');
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [audit, setAudit] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [profileDraft, setProfileDraft] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [assignServiceId, setAssignServiceId] = useState('');
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignAgreedPrice, setAssignAgreedPrice] = useState('');
  const [serviceDraft, setServiceDraft] = useState(null);
  const [productionSites, setProductionSites] = useState([]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      // Todo en paralelo: la ficha es la pantalla más pesada de la operación y
      // encadenar cinco peticiones la haría sentir lenta sin necesidad.
      const [detail, metricsResult, messagesResult, auditResult, catalogResult, productionSitesResult] = await Promise.all([
        adminService.getClient(clientId),
        adminService.metrics(clientId),
        adminService.listMessages(clientId),
        adminService.audit({ clientId, limit: 60 }),
        adminService.listCatalog(),
        adminService.listProductionSites().catch(() => ({ sites: [] }))
      ]);
      setData(detail);
      setMetrics(metricsResult);
      setMessages(messagesResult.messages || []);
      setAudit(auditResult.events || []);
      // Además de lo que ya está a la venta, MARKETOS puede asignar productos
      // operativos (CRM, POS o KDS) antes de publicar su precio en el portal.
      setCatalog((catalogResult.services || []).filter((s) => s.active || s.definition?.internal_assignable));
      setProductionSites(productionSitesResult.sites || []);
      setProfileDraft({
        display_name: detail.profile?.display_name || detail.client?.name || '',
        industry: detail.profile?.industry || '',
        service_status: detail.profile?.service_status || 'onboarding',
        start_date: detail.profile?.start_date || '',
        maintenance_plan: detail.profile?.maintenance_plan || '',
        assigned_to: detail.profile?.assigned_to || '',
        primary_domain: detail.profile?.primary_domain || '',
        whatsapp: detail.profile?.whatsapp || '',
        meta_ads_status: detail.profile?.meta_ads_status || '',
        notes: detail.profile?.notes || ''
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (open) { setTab('resumen'); setServiceDraft(null); load(); } }, [open, load]);

  const run = async (fn, successMessage) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice({ tone: 'success', text: successMessage });
      await load();
      onChanged?.();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const client = data?.client;
  const services = data?.services || [];
  const requests = data?.requests || [];
  const pending = requests.filter((r) => ['pending', 'reviewing', 'returned'].includes(r.status));
  const lastExecuted = requests.find((r) => r.status === 'completed');
  const selectedCatalogService = catalog.find((service) => String(service.id) === String(assignServiceId));
  const selectedPlans = selectedCatalogService?.definition?.plans || [];
  const selectedPlan = selectedPlans.find((plan) => plan.id === assignPlanId) || selectedPlans[0];
  const selectedPlanNeedsPrice = Boolean(selectedPlan && selectedPlan.price == null);
  const serviceDraftCatalog = serviceDraft
    ? catalog.find((service) => String(service.id) === String(serviceDraft.catalog_service_id))
    : null;
  const serviceDraftPlans = serviceDraftCatalog?.definition?.plans || [];
  const selectedProductIsOperational = Boolean(selectedCatalogService?.definition?.crm || selectedCatalogService?.definition?.pos || selectedCatalogService?.definition?.kds);
  const websiteChecklistTotal = WEBSITE_CHECKLIST_FIELDS.length;
  const websiteChecklistCompleted = serviceDraft
    ? WEBSITE_CHECKLIST_FIELDS.filter(([key]) => !!serviceDraft.checklist?.[key]).length
    : 0;
  const websiteProgress = websiteChecklistTotal
    ? Math.round((websiteChecklistCompleted / websiteChecklistTotal) * 100)
    : 0;
  const nextWebsiteTask = serviceDraft
    ? WEBSITE_CHECKLIST_FIELDS.find(([key]) => !serviceDraft.checklist?.[key])
    : null;
  const activeWebsitePhaseId = serviceDraft
    ? WEBSITE_PROCESS_PHASES.find((phase) => phase.tasks.some(([key]) => !serviceDraft.checklist?.[key]))?.id
    : null;
  const suggestedStage = serviceDraft ? suggestedWebsiteStage(serviceDraft.checklist) : 'contracted';
  const suggestedStageLabel = WEBSITE_STAGE_OPTIONS.find((option) => option.value === suggestedStage)?.label || 'Contrato confirmado';

  const openServiceDraft = (service) => {
    const config = service.config || {};
    const internal = config.internal_process || {};
    const project = service.web_project || null;
    const catalogPlans = catalog.find((item) => String(item.id) === String(service.id))?.definition?.plans || [];
    setServiceDraft({
      assignment_id: service.assignment_id,
      catalog_service_id: service.id,
      name: service.name,
      status: service.service_status,
      agreed_price: service.agreed_price ?? service.base_price ?? '',
      plan_id: config.plan_id || config.contract?.plan_id || catalogPlans[0]?.id || '',
      plan_label: config.contract?.plan_label || 'Sin modalidad registrada',
      domain_name: config.website?.domain_name || '',
      domain_status: config.website?.domain_status || 'pending',
      hosting_status: config.website?.hosting_status || 'pending',
      publication_url: config.website?.publication_url || '',
      progress_stage: config.client_progress?.stage || 'contracted',
      progress_label: config.client_progress?.label || 'Contrato confirmado',
      progress_detail: config.client_progress?.detail || '',
      responsible: internal.responsible || data?.profile?.assigned_to || '',
      priority: internal.priority || 'normal',
      start_date: internal.start_date || data?.profile?.start_date || '',
      target_publication_date: internal.target_publication_date || '',
      review_sent_at: internal.review_sent_at || '',
      approved_at: internal.approved_at || '',
      published_at: internal.published_at || '',
      warranty_end_date: internal.warranty_end_date || '',
      renewal_date: internal.renewal_date || '',
      internal_notes: internal.internal_notes || '',
      web_project_id: project?.id || null,
      project_code: project?.project_code || '',
      web_status: project?.status || 'setup',
      intake_status: project?.intake_status || 'not_started',
      static_site_id: project?.static_site_id ? String(project.static_site_id) : '',
      preview_path: project?.preview_path || '',
      current_version: project?.current_version || 0,
      assigned_agent: project?.assigned_agent || '',
      checklist: service.checklist || {},
      original_config: config
    });
  };

  const saveServiceDraft = () => run(async () => {
    const stageLabel = WEBSITE_STAGE_OPTIONS.find((option) => option.value === serviceDraft.progress_stage)?.label || serviceDraft.progress_label;
    const plan = serviceDraftPlans.find((item) => item.id === serviceDraft.plan_id);
    const contract = plan ? {
      ...(serviceDraft.original_config.contract || {}),
      plan_id: plan.id,
      plan_label: plan.label,
      amount: plan.price == null ? null : Number(plan.price),
      billing_cycle: plan.billing_cycle || null,
      access_plan: plan.access_plan || null
    } : (serviceDraft.original_config.contract || null);
    await adminService.updateAssignment(serviceDraft.assignment_id, {
      status: serviceDraft.status,
      agreed_price: Number(serviceDraft.agreed_price || 0),
      config: {
        plan_id: plan?.id || serviceDraft.original_config.plan_id || null,
        contract,
        website: {
          ...(serviceDraft.original_config.website || {}),
          domain_name: serviceDraft.domain_name.trim(),
          domain_status: serviceDraft.domain_status,
          hosting_status: serviceDraft.hosting_status,
          publication_url: serviceDraft.publication_url.trim()
        },
        client_progress: {
          stage: serviceDraft.progress_stage,
          label: stageLabel,
          detail: serviceDraft.progress_detail.trim()
        },
        internal_process: {
          ...(serviceDraft.original_config.internal_process || {}),
          responsible: serviceDraft.responsible.trim(),
          priority: serviceDraft.priority,
          start_date: serviceDraft.start_date,
          target_publication_date: serviceDraft.target_publication_date,
          review_sent_at: serviceDraft.review_sent_at,
          approved_at: serviceDraft.approved_at,
          published_at: serviceDraft.published_at,
          warranty_end_date: serviceDraft.warranty_end_date,
          renewal_date: serviceDraft.renewal_date,
          internal_notes: serviceDraft.internal_notes.trim()
        }
      },
      checklist: serviceDraft.checklist
    });
    if (serviceDraft.web_project_id) {
      await adminService.updateWebProject(serviceDraft.web_project_id, {
        status: serviceDraft.web_status,
        intake_status: serviceDraft.intake_status,
        static_site_id: serviceDraft.static_site_id || null,
        current_version: Number(serviceDraft.current_version || 0),
        assigned_to: serviceDraft.responsible.trim(),
        assigned_agent: serviceDraft.assigned_agent.trim(),
        domain_name: serviceDraft.domain_name.trim(),
        domain_status: serviceDraft.domain_status,
        hosting_status: serviceDraft.hosting_status,
        production_url: serviceDraft.publication_url.trim(),
        client_progress_detail: serviceDraft.progress_detail.trim(),
        client_approved_at: serviceDraft.approved_at || null,
        published_at: serviceDraft.published_at || null,
        warranty_ends_at: serviceDraft.warranty_end_date || null,
        renewal_at: serviceDraft.renewal_date || null,
        internal_notes: serviceDraft.internal_notes.trim()
      });
    }
    setServiceDraft(null);
  }, 'Operación del producto actualizada. El cliente verá únicamente el progreso publicable.');

  // Un producto contratado se clasifica por la `definition` del catálogo, no
  // por su nombre: así un producto nuevo llamado "Landing Premium" aparece en
  // la pestaña correcta sin tocar este archivo.
  const servicesOfKind = (predicate) => services.filter((s) => {
    const definition = (() => { try { return JSON.parse(s.definition_json || '{}'); } catch (_) { return {}; } })();
    return predicate(definition, s);
  });

  const TABS = [
    { id: 'resumen', label: 'Resumen', icon: <Activity className="h-4 w-4" /> },
    { id: 'productos', label: 'Productos', icon: <Package className="h-4 w-4" />, count: services.length },
    { id: 'web', label: 'Página Web', icon: <Globe className="h-4 w-4" /> },
    { id: 'crm', label: 'CRM', icon: <Users className="h-4 w-4" /> },
    { id: 'pos', label: 'Punto de Venta', icon: <ShoppingCart className="h-4 w-4" /> },
    { id: 'campanas', label: 'Campañas', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'solicitudes', label: 'Solicitudes', icon: <ClipboardList className="h-4 w-4" />, count: pending.length },
    { id: 'metricas', label: 'Métricas', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'auditoria', label: 'Auditoría', icon: <ScrollText className="h-4 w-4" /> },
    { id: 'mensajes', label: 'Mensajes', icon: <MessageSquare className="h-4 w-4" />, count: messages.length },
    { id: 'configuracion', label: 'Configuración', icon: <Settings2 className="h-4 w-4" /> }
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={client?.name || 'Cliente'}
      subtitle={client ? `${client.email} · ${client.category || 'Sin categoría'}` : undefined}
    >
      <AsyncBoundary loading={loading} error={error} onRetry={load} loadingLabel="Cargando ficha del cliente…">
        {client && (
          <div className="space-y-5">
            {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

            <Tabs tabs={TABS} value={tab} onChange={setTab} />

            {tab === 'resumen' && (
              <div className="space-y-5 animate-fade-in">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <InfoRow icon={<Building2 className="h-4 w-4" />} label="Empresa" value={data.profile?.display_name || client.name} />
                  <InfoRow icon={<Mail className="h-4 w-4" />} label="Correo" value={client.email} />
                  <InfoRow icon={<Phone className="h-4 w-4" />} label="Teléfono" value={client.phone || data.profile?.whatsapp || '—'} />
                  <InfoRow label="Estado" value={<Badge tone={client.status === 'active' ? 'green' : 'gold'}>{client.status}</Badge>} />
                  <InfoRow label="Fecha de inicio" value={formatDate(data.profile?.start_date)} />
                  <InfoRow label="Responsable MARKETOS" value={data.profile?.assigned_to || 'Sin asignar'} />
                  <InfoRow label="Solicitudes pendientes" value={pending.length ? `${pending.length} por revisar` : 'Ninguna'} />
                  <InfoRow label="Último cambio ejecutado" value={lastExecuted ? `${lastExecuted.title} · ${formatDate(lastExecuted.executed_at)}` : 'Todavía ninguno'} />
                  <InfoRow label="Última actividad" value={formatDateTime(audit[0]?.created_at)} />
                </div>

                <div>
                  <SectionTitle>Productos contratados</SectionTitle>
                  {services.length === 0
                    ? <p className="text-sm text-navy-900/50">Este cliente todavía no tiene productos asignados.</p>
                    : (
                      <div className="flex flex-wrap gap-2">
                        {services.map((service) => (
                          <Badge key={service.assignment_id} tone={SERVICE_STATUS[service.service_status]?.tone || 'navy'}>
                            {service.name} · {SERVICE_STATUS[service.service_status]?.label || service.service_status}
                          </Badge>
                        ))}
                      </div>
                    )}
                </div>

                {data.profile?.notes && (
                  <div>
                    <SectionTitle>Notas internas</SectionTitle>
                    <p className="whitespace-pre-wrap rounded-xl bg-cream p-4 text-sm text-navy-900/70">{data.profile.notes}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'productos' && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex flex-wrap items-end gap-2 rounded-xl bg-cream p-4">
                  <Field label="Asignar un producto del catálogo" htmlFor="assign-service">
                    <Select
                      id="assign-service"
                      value={assignServiceId}
                      onChange={(e) => {
                        const nextId = e.target.value;
                        const nextService = catalog.find((service) => String(service.id) === String(nextId));
                        const firstPlan = nextService?.definition?.plans?.[0];
                        setAssignServiceId(nextId);
                        setAssignPlanId(firstPlan?.id || '');
                        setAssignAgreedPrice(firstPlan?.price ?? (firstPlan ? '' : (nextService?.base_price ?? '')));
                      }}
                    >
                      <option value="">Selecciona un producto</option>
                      {catalog.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name} — {service.base_price == null ? 'Precio por definir' : formatMoney(service.base_price)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {selectedPlans.length > 0 && (
                    <Field label="Modalidad contratada" htmlFor="assign-plan">
                      <Select id="assign-plan" value={selectedPlan?.id || ''} onChange={(e) => setAssignPlanId(e.target.value)}>
                        {selectedPlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.label}{plan.price == null ? ' — valor según acuerdo' : ` — ${formatMoney(plan.price)}`}</option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  {assignServiceId && (
                    <Field label="Valor acordado" htmlFor="assign-agreed-price" hint={selectedPlanNeedsPrice ? 'Obligatorio: el valor del POS se confirma según el sector y el acuerdo comercial.' : undefined}>
                      <Input
                        id="assign-agreed-price"
                        type="number"
                        min="0"
                        step="1"
                        value={assignAgreedPrice}
                        onChange={(e) => setAssignAgreedPrice(e.target.value)}
                        placeholder="Valor mensual o acordado"
                      />
                    </Field>
                  )}
                  <Button
                    variant="secondary"
                    size="lg"
                    icon={<Package className="h-4 w-4" />}
                    className="min-w-[15rem] !bg-[#0B2460] !text-white shadow-lg ring-1 ring-[#0B2460] hover:!bg-[#086CA8] hover:ring-[#086CA8] disabled:!bg-navy-100 disabled:!text-navy-700 disabled:!opacity-100"
                    disabled={!assignServiceId || busy || (selectedPlanNeedsPrice && assignAgreedPrice === '')}
                    title={!assignServiceId ? 'Primero selecciona un producto' : selectedPlanNeedsPrice && assignAgreedPrice === '' ? 'Indica primero el valor acordado para este plan POS' : 'Asignar este producto al cliente'}
                    onClick={() => run(
                      async () => {
                        await adminService.assignProduct(clientId, {
                          service_id: Number(assignServiceId),
                          // CRM, POS y KDS son accesos operativos: al asignarlos
                          // desde MARKETOS se habilitan de inmediato. Los demás
                          // productos conservan el flujo de planificación.
                          status: selectedProductIsOperational ? 'active' : 'planned',
                          agreed_price: assignAgreedPrice === '' ? (selectedPlan?.price ?? selectedCatalogService?.base_price) : Number(assignAgreedPrice),
                          config: selectedPlan ? { plan_id: selectedPlan.id } : {}
                        });
                        setAssignServiceId('');
                        setAssignPlanId('');
                        setAssignAgreedPrice('');
                      },
                      selectedProductIsOperational
                        ? 'Producto operativo asignado y acceso habilitado. El cliente debe volver a entrar para ver su nueva sección.'
                        : 'Producto asignado. Queda en estado Planificado.'
                    )}
                  >
                    {selectedProductIsOperational ? 'Asignar y habilitar' : 'Asignar producto'}
                  </Button>
                </div>

                <DataTable
                  rowKey={(row) => row.assignment_id}
                  emptyMessage="Sin productos contratados"
                  rows={services}
                  columns={[
                    { key: 'name', header: 'Producto', render: (row) => <span className="font-semibold">{row.name}</span> },
                    { key: 'plan', header: 'Modalidad', render: (row) => row.config?.contract?.plan_label || 'Sin definir' },
                    { key: 'price', header: 'Precio', render: (row) => formatMoney(row.agreed_price ?? row.base_price) },
                    {
                      key: 'status',
                      header: 'Estado',
                      render: (row) => (
                        <Select
                          value={row.service_status}
                          onChange={(event) => run(
                            () => adminService.updateAssignment(row.assignment_id, { status: event.target.value }),
                            'Estado del producto actualizado.'
                          )}
                        >
                          {SERVICE_STATE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </Select>
                      )
                    },
                    {
                      key: 'actions',
                      header: '',
                      render: (row) => <Button variant="soft" size="sm" onClick={() => openServiceDraft(row)}>Gestionar</Button>
                    }
                  ]}
                />

                {serviceDraft && (
                  <div className="space-y-5 rounded-2xl border border-sky/30 bg-sky-50/40 p-4 sm:p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-sky">Operación interna</p>
                        <h3 className="text-lg font-bold text-navy-900">{serviceDraft.name}</h3>
                        <p className="text-sm text-navy-900/60">{serviceDraft.plan_label}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setServiceDraft(null)}>Cerrar</Button>
                    </div>

                    <div className="grid gap-3 rounded-2xl bg-navy-900 p-4 text-white lg:grid-cols-[1fr_auto_auto] lg:items-center">
                      <div>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-bold">Avance operativo</p>
                          <p className="text-sm font-extrabold text-sky-100">{websiteProgress}%</p>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/15">
                          <div className="h-full rounded-full bg-gradient-to-r from-magenta to-sky transition-all" style={{ width: `${websiteProgress}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-white/65">
                          {websiteChecklistCompleted} de {websiteChecklistTotal} tareas · Próxima: {nextWebsiteTask?.[1] || 'Procedimiento completado'}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white/10 px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-white/55">Etapa sugerida al cliente</p>
                        <p className="mt-1 text-sm font-bold">
                          {serviceDraft.web_project_id
                            ? (WEB_PROJECT_STATUS_OPTIONS.find(([value]) => value === serviceDraft.web_status)?.[1] || 'Proyecto creado')
                            : suggestedStageLabel}
                        </p>
                      </div>
                      {!serviceDraft.web_project_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setServiceDraft({
                            ...serviceDraft,
                            progress_stage: suggestedStage,
                            progress_label: suggestedStageLabel
                          })}
                        >
                          Aplicar sugerencia
                        </Button>
                      )}
                    </div>

                    {serviceDraft.web_project_id && (
                      <div className="rounded-2xl border border-magenta/20 bg-white p-4">
                        <SectionTitle hint="Esta relación evita confundir el cliente, su producto y el ZIP publicado.">Proyecto web vinculado</SectionTitle>
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <Badge tone="magenta">{serviceDraft.project_code}</Badge>
                          <Badge tone="navy">Producción mediante ZIP</Badge>
                          {serviceDraft.preview_path && <Badge tone="sky">Vista previa preparada</Badge>}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          <Field label="Estado real del proyecto">
                            <Select value={serviceDraft.web_status} onChange={(e) => setServiceDraft({ ...serviceDraft, web_status: e.target.value })}>
                              {WEB_PROJECT_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </Select>
                          </Field>
                          <Field label="Información del cliente" hint="En la Fase 2 se actualizará automáticamente desde el formulario.">
                            <Select value={serviceDraft.intake_status} onChange={(e) => setServiceDraft({ ...serviceDraft, intake_status: e.target.value })}>
                              {WEB_INTAKE_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </Select>
                          </Field>
                          <Field label="Sitio ZIP desplegado" hint="Elige por nombre; MARKETOS guarda la relación técnica automáticamente.">
                            <Select
                              value={serviceDraft.static_site_id}
                              onChange={(e) => {
                                const site = productionSites.find((item) => String(item.id) === e.target.value);
                                setServiceDraft({
                                  ...serviceDraft,
                                  static_site_id: e.target.value,
                                  preview_path: site?.preview_path || ''
                                });
                              }}
                            >
                              <option value="">Todavía no hay sitio vinculado</option>
                              {productionSites.map((site) => (
                                <option key={site.id} value={site.id}>
                                  {site.name} · /s/{site.slug}{site.status !== 'active' ? ' · pausado' : ''}
                                </option>
                              ))}
                            </Select>
                          </Field>
                          <Field label="Versión actual">
                            <Input type="number" min="0" step="1" value={serviceDraft.current_version} onChange={(e) => setServiceDraft({ ...serviceDraft, current_version: e.target.value })} />
                          </Field>
                          <Field label="Agente o herramienta asignada" hint="Ejemplo: Codex, diseñador UI o agente web.">
                            <Input value={serviceDraft.assigned_agent} onChange={(e) => setServiceDraft({ ...serviceDraft, assigned_agent: e.target.value })} placeholder="Sin asignar" />
                          </Field>
                          <Field label="Vista previa">
                            <Input value={serviceDraft.preview_path} readOnly placeholder="Se completa al vincular un ZIP" />
                          </Field>
                        </div>
                        {productionSites.length === 0 && (
                          <Alert tone="info" title="Aún no hay sitios ZIP disponibles">
                            Cuando subas el primer sitio desde la cuenta interna de Producción Web, aparecerá aquí para vincularlo sin escribir códigos ni rutas.
                          </Alert>
                        )}
                      </div>
                    )}

                    <div className="rounded-2xl border border-navy-50 bg-white p-4">
                      <SectionTitle hint="Estos datos son privados y organizan el trabajo del equipo.">Control interno del proyecto</SectionTitle>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Field label="Responsable MARKETOS">
                          <Input value={serviceDraft.responsible} onChange={(e) => setServiceDraft({ ...serviceDraft, responsible: e.target.value })} placeholder="Nombre del responsable" />
                        </Field>
                        <Field label="Prioridad">
                          <Select value={serviceDraft.priority} onChange={(e) => setServiceDraft({ ...serviceDraft, priority: e.target.value })}>
                            <option value="low">Baja</option>
                            <option value="normal">Normal</option>
                            <option value="high">Alta</option>
                            <option value="urgent">Urgente</option>
                          </Select>
                        </Field>
                        <Field label="Inicio">
                          <Input type="date" value={serviceDraft.start_date} onChange={(e) => setServiceDraft({ ...serviceDraft, start_date: e.target.value })} />
                        </Field>
                        <Field label="Publicación objetivo">
                          <Input type="date" value={serviceDraft.target_publication_date} onChange={(e) => setServiceDraft({ ...serviceDraft, target_publication_date: e.target.value })} />
                        </Field>
                        <Field label="Vista previa enviada">
                          <Input type="date" value={serviceDraft.review_sent_at} onChange={(e) => setServiceDraft({ ...serviceDraft, review_sent_at: e.target.value })} />
                        </Field>
                        <Field label="Aprobación recibida">
                          <Input type="date" value={serviceDraft.approved_at} onChange={(e) => setServiceDraft({ ...serviceDraft, approved_at: e.target.value })} />
                        </Field>
                        <Field label="Publicación real">
                          <Input type="date" value={serviceDraft.published_at} onChange={(e) => setServiceDraft({ ...serviceDraft, published_at: e.target.value })} />
                        </Field>
                        <Field label="Fin de garantía">
                          <Input type="date" value={serviceDraft.warranty_end_date} onChange={(e) => setServiceDraft({ ...serviceDraft, warranty_end_date: e.target.value })} />
                        </Field>
                        <Field label="Próxima renovación">
                          <Input type="date" value={serviceDraft.renewal_date} onChange={(e) => setServiceDraft({ ...serviceDraft, renewal_date: e.target.value })} />
                        </Field>
                      </div>
                      <div className="mt-4">
                        <Field label="Notas internas" hint="No aparecen en el portal del cliente.">
                          <Textarea rows={3} value={serviceDraft.internal_notes} onChange={(e) => setServiceDraft({ ...serviceDraft, internal_notes: e.target.value })} placeholder="Bloqueos, decisiones, datos pendientes o acuerdos internos…" />
                        </Field>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {serviceDraftPlans.length > 0 && (
                        <Field label="Plan contratado" hint="Solo MARKETOS puede modificarlo. Al guardar, se aplican los permisos del plan al próximo ingreso del cliente.">
                          <Select
                            value={serviceDraft.plan_id}
                            onChange={(e) => {
                              const nextPlan = serviceDraftPlans.find((item) => item.id === e.target.value);
                              setServiceDraft({
                                ...serviceDraft,
                                plan_id: e.target.value,
                                agreed_price: nextPlan?.price == null ? serviceDraft.agreed_price : nextPlan.price
                              });
                            }}
                          >
                            {serviceDraftPlans.map((plan) => (
                              <option key={plan.id} value={plan.id}>
                                {plan.label}{plan.price == null ? ' — valor según acuerdo' : ` — ${formatMoney(plan.price)}`}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      )}
                      <Field label="Estado del producto">
                        <Select value={serviceDraft.status} onChange={(e) => setServiceDraft({ ...serviceDraft, status: e.target.value })}>
                          {SERVICE_STATE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </Select>
                      </Field>
                      <Field label="Precio acordado">
                        <Input type="number" min="0" step="1" value={serviceDraft.agreed_price} onChange={(e) => setServiceDraft({ ...serviceDraft, agreed_price: e.target.value })} />
                      </Field>
                      <Field label="Etapa visible para el cliente">
                        {serviceDraft.web_project_id
                          ? <Input value={WEB_PROJECT_STATUS_OPTIONS.find(([value]) => value === serviceDraft.web_status)?.[1] || 'Proyecto creado'} readOnly />
                          : (
                            <Select
                              value={serviceDraft.progress_stage}
                              onChange={(e) => setServiceDraft({
                                ...serviceDraft,
                                progress_stage: e.target.value,
                                progress_label: WEBSITE_STAGE_OPTIONS.find((option) => option.value === e.target.value)?.label || ''
                              })}
                            >
                              {WEBSITE_STAGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </Select>
                          )}
                      </Field>
                      <Field label="Dominio">
                        <Input value={serviceDraft.domain_name} onChange={(e) => setServiceDraft({ ...serviceDraft, domain_name: e.target.value })} placeholder="negocio.com" />
                      </Field>
                      <Field label="Estado del dominio">
                        <Select value={serviceDraft.domain_status} onChange={(e) => setServiceDraft({ ...serviceDraft, domain_status: e.target.value })}>
                          <option value="pending">Pendiente</option>
                          <option value="reserved">Reservado</option>
                          <option value="configuring">Configurando</option>
                          <option value="active">Activo</option>
                          <option value="expired">Vencido</option>
                        </Select>
                      </Field>
                      <Field label="Estado del hosting">
                        <Select value={serviceDraft.hosting_status} onChange={(e) => setServiceDraft({ ...serviceDraft, hosting_status: e.target.value })}>
                          <option value="pending">Pendiente</option>
                          <option value="preparing">Preparando</option>
                          <option value="active">Activo</option>
                          <option value="suspended">Suspendido</option>
                        </Select>
                      </Field>
                    </div>

                    <Field label="Enlace publicado">
                      <Input value={serviceDraft.publication_url} onChange={(e) => setServiceDraft({ ...serviceDraft, publication_url: e.target.value })} placeholder="https://negocio.com" />
                    </Field>
                    <Field label="Mensaje de avance para el cliente" hint="Este texto sí aparece en Mi Proyecto. No escribas notas técnicas.">
                      <Textarea rows={2} value={serviceDraft.progress_detail} onChange={(e) => setServiceDraft({ ...serviceDraft, progress_detail: e.target.value })} />
                    </Field>

                    <div>
                      <SectionTitle hint="Sigue las fases en orden. Todo este bloque es privado para MARKETOS.">Procedimiento tras bambalinas</SectionTitle>
                      <div className="space-y-3">
                        {WEBSITE_PROCESS_PHASES.map((phase) => {
                          const completed = phase.tasks.filter(([key]) => !!serviceDraft.checklist[key]).length;
                          const complete = completed === phase.tasks.length;
                          return (
                            <details key={phase.id} open={phase.id === activeWebsitePhaseId} className="group overflow-hidden rounded-2xl border border-navy-50 bg-white">
                              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 bg-cream px-4 py-3.5">
                                <div className="flex min-w-0 items-start gap-3">
                                  <span className={`mt-0.5 rounded-xl p-2 ${complete ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky'}`}>
                                    {complete ? <CheckCircle2 className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
                                  </span>
                                  <div className="min-w-0">
                                    <p className="font-bold text-navy-900">{phase.title}</p>
                                    <p className="mt-0.5 text-xs text-navy-900/55">{phase.purpose}</p>
                                  </div>
                                </div>
                                <Badge tone={complete ? 'green' : 'sky'}>{completed}/{phase.tasks.length}</Badge>
                              </summary>
                              <div className="grid gap-2 border-t border-navy-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
                                {phase.tasks.map(([key, label]) => (
                                  <label key={key} className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 text-sm transition ${serviceDraft.checklist[key] ? 'border-emerald-100 bg-emerald-50/60 text-navy-900' : 'border-navy-50 bg-white text-navy-900/75 hover:border-sky-100'}`}>
                                    <input
                                      type="checkbox"
                                      checked={!!serviceDraft.checklist[key]}
                                      onChange={(e) => setServiceDraft({
                                        ...serviceDraft,
                                        checklist: { ...serviceDraft.checklist, [key]: e.target.checked }
                                      })}
                                      className="mt-0.5 h-4 w-4 rounded border-navy-100 text-magenta focus:ring-magenta"
                                    />
                                    <span>{label}</span>
                                  </label>
                                ))}
                              </div>
                            </details>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex justify-end border-t border-sky-100 pt-4">
                      <Button
                        variant="secondary"
                        size="lg"
                        icon={<Save className="h-4 w-4" />}
                        className="min-w-[13rem] shadow-card disabled:!bg-navy-100 disabled:!text-navy-700 disabled:!opacity-100"
                        loading={busy}
                        onClick={saveServiceDraft}
                      >
                        Guardar operación
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'web' && (
              <ProductoTab
                titulo="Páginas Web"
                vacio="Este cliente no tiene productos de página web contratados."
                descripcionVacia="Desde aquí podrás seguir el dominio, el avance de construcción, la vista previa y la publicación cuando se asigne una página web."
                icono={<Globe className="h-6 w-6" />}
                onManageProducts={() => setTab('productos')}
                servicios={servicesOfKind((d) => d.kind === 'website' || d.kind === 'landing' || d.kind === 'system')}
                extra={data.profile?.primary_domain && <InfoRow icon={<Globe className="h-4 w-4" />} label="Dominio principal" value={data.profile.primary_domain} />}
              />
            )}

            {tab === 'crm' && (
              <ProductoTab
                titulo="CRM"
                vacio="Este cliente no tiene CRM contratado."
                descripcionVacia="Al asignar CRM, esta vista mostrará contactos, conversaciones, embudos y automatizaciones de este cliente."
                icono={<Users className="h-6 w-6" />}
                onManageProducts={() => setTab('productos')}
                servicios={servicesOfKind((d) => d.crm)}
                extra={
                  <div className="grid gap-3 sm:grid-cols-3">
                    <StatCard label="Contactos" value={metrics?.totals?.contacts ?? 0} tone="sky" />
                    <StatCard label="Conversaciones" value={metrics?.totals?.conversations ?? 0} tone="grape" />
                    <StatCard label="Automatizaciones activas" value={metrics?.totals?.active_campaigns ?? 0} tone="gold" />
                  </div>
                }
              />
            )}

            {tab === 'pos' && (
              <ProductoTab
                titulo="Punto de Venta"
                vacio="Este cliente no tiene punto de venta contratado."
                descripcionVacia="Al asignar POS o KDS, aquí verás las ventas, ingresos y el estado operativo del punto de venta."
                icono={<ShoppingCart className="h-6 w-6" />}
                onManageProducts={() => setTab('productos')}
                servicios={servicesOfKind((d, s) => d.kind === 'pos' || s.category === 'punto-de-venta')}
                extra={
                  <div className="grid gap-3 sm:grid-cols-2">
                    <StatCard label="Ventas" value={metrics?.totals?.sales ?? 0} tone="green" />
                    <StatCard label="Ingresos" value={formatMoney(metrics?.totals?.revenue)} tone="magenta" />
                  </div>
                }
              />
            )}

            {tab === 'campanas' && (
              <ProductoTab
                titulo="Campañas"
                vacio="Este cliente no tiene campañas activas."
                descripcionVacia="Cuando MARKETOS active una campaña, esta vista reunirá su estado, resultados y próximos pasos."
                icono={<BarChart3 className="h-6 w-6" />}
                onManageProducts={() => setTab('productos')}
                servicios={servicesOfKind((d, s) => s.category === 'marketing' || d.reports)}
                extra={data.profile?.meta_ads_status && <InfoRow label="Estado de Meta Ads" value={data.profile.meta_ads_status} />}
              />
            )}

            {tab === 'solicitudes' && (
              <div className="animate-fade-in">
                <DataTable
                  rowKey={(row) => row.id}
                  emptyMessage="Este cliente no ha enviado solicitudes"
                  rows={requests}
                  columns={[
                    { key: 'title', header: 'Solicitud', render: (row) => <span className="font-semibold">{row.title}</span> },
                    { key: 'status', header: 'Estado', render: (row) => <Badge tone={REQUEST_STATUS[row.status]?.tone}>{REQUEST_STATUS[row.status]?.label || row.status}</Badge> },
                    { key: 'created_at', header: 'Fecha', render: (row) => formatDate(row.created_at) }
                  ]}
                />
              </div>
            )}

            {tab === 'metricas' && (
              <div className="space-y-4 animate-fade-in">
                <StatGrid>
                  <StatCard label="Contactos" value={metrics?.totals?.contacts ?? 0} tone="sky" />
                  <StatCard label="Conversaciones" value={metrics?.totals?.conversations ?? 0} tone="grape" />
                  <StatCard label="Ventas" value={metrics?.totals?.sales ?? 0} tone="green" />
                  <StatCard label="Ingresos" value={formatMoney(metrics?.totals?.revenue)} tone="magenta" />
                  <StatCard label="Páginas activas" value={metrics?.totals?.active_sites ?? 0} tone="navy" />
                  <StatCard label="Campañas activas" value={metrics?.totals?.active_campaigns ?? 0} tone="gold" />
                  <StatCard label="Solicitudes pendientes" value={metrics?.totals?.pending_requests ?? 0} tone="gold" />
                  <StatCard label="Cambios completados" value={metrics?.totals?.completed_changes ?? 0} tone="green" />
                </StatGrid>
                <p className="text-xs text-navy-900/45">
                  Estas cifras corresponden únicamente a este cliente.
                </p>
              </div>
            )}

            {tab === 'auditoria' && (
              <div className="animate-fade-in">
                <Timeline
                  items={audit.map((event) => ({
                    id: event.id,
                    title: EVENT_LABELS[event.event_type] || event.event_type,
                    description: event.request_title,
                    meta: `${ACTOR_LABELS[event.actor_type] || event.actor_type} · ${formatDateTime(event.created_at)}`
                  }))}
                />
              </div>
            )}

            {tab === 'mensajes' && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(event) => setNewMessage(event.target.value)}
                    placeholder="Escribe una actualización para el cliente…"
                  />
                  <Button
                    icon={<MessageSquare className="h-4 w-4" />}
                    disabled={!newMessage.trim() || busy}
                    onClick={() => run(
                      async () => { await adminService.sendMessage(clientId, newMessage.trim()); setNewMessage(''); },
                      'Mensaje enviado. El cliente lo verá en Mi Proyecto.'
                    )}
                  >
                    Enviar
                  </Button>
                </div>
                <div className="space-y-2">
                  {messages.length === 0
                    ? <p className="text-sm text-navy-900/50">Sin mensajes todavía.</p>
                    : messages.map((message) => (
                      <div key={message.id} className={`rounded-xl p-3 text-sm ${message.author_type === 'client' ? 'bg-navy-50' : 'bg-sky-50'}`}>
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-navy-900/50">
                          {message.author_type === 'client' ? 'Cliente' : 'MARKETOS'} · {formatDateTime(message.created_at)}
                        </p>
                        <p className="whitespace-pre-wrap text-navy-900">{message.body}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {tab === 'configuracion' && profileDraft && (
              <div className="space-y-4 animate-fade-in">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nombre comercial"><Input value={profileDraft.display_name} onChange={(e) => setProfileDraft({ ...profileDraft, display_name: e.target.value })} /></Field>
                  <Field label="Sector"><Input value={profileDraft.industry} onChange={(e) => setProfileDraft({ ...profileDraft, industry: e.target.value })} placeholder="Ej.: Restaurante" /></Field>
                  <Field label="Estado del servicio">
                    <Select value={profileDraft.service_status} onChange={(e) => setProfileDraft({ ...profileDraft, service_status: e.target.value })}>
                      <option value="onboarding">En implementación</option>
                      <option value="active">Activo</option>
                      <option value="paused">Pausado</option>
                      <option value="finished">Finalizado</option>
                    </Select>
                  </Field>
                  <Field label="Fecha de inicio"><Input type="date" value={profileDraft.start_date || ''} onChange={(e) => setProfileDraft({ ...profileDraft, start_date: e.target.value })} /></Field>
                  <Field label="Plan de mantenimiento"><Input value={profileDraft.maintenance_plan} onChange={(e) => setProfileDraft({ ...profileDraft, maintenance_plan: e.target.value })} /></Field>
                  <Field label="Responsable MARKETOS"><Input value={profileDraft.assigned_to} onChange={(e) => setProfileDraft({ ...profileDraft, assigned_to: e.target.value })} /></Field>
                  <Field label="Dominio principal"><Input value={profileDraft.primary_domain} onChange={(e) => setProfileDraft({ ...profileDraft, primary_domain: e.target.value })} placeholder="micliente.com" /></Field>
                  <Field label="WhatsApp"><Input value={profileDraft.whatsapp} onChange={(e) => setProfileDraft({ ...profileDraft, whatsapp: e.target.value })} /></Field>
                  <Field label="Estado de Meta Ads"><Input value={profileDraft.meta_ads_status} onChange={(e) => setProfileDraft({ ...profileDraft, meta_ads_status: e.target.value })} /></Field>
                </div>
                <Field label="Notas internas" hint="Solo las ve el equipo de MARKETOS. El cliente nunca las lee.">
                  <Textarea rows={4} value={profileDraft.notes} onChange={(e) => setProfileDraft({ ...profileDraft, notes: e.target.value })} />
                </Field>
                <div className="flex justify-end">
                  <Button
                    icon={<Save className="h-4 w-4" />}
                    loading={busy}
                    onClick={() => run(() => adminService.saveClientProfile(clientId, profileDraft), 'Ficha actualizada.')}
                  >
                    Guardar ficha
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </AsyncBoundary>
    </Modal>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-navy-50 px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-navy-900/45">{icon}{label}</p>
      <div className="mt-1 break-words text-sm font-semibold text-navy-900">{value || '—'}</div>
    </div>
  );
}

// Vista común de las pestañas por producto: lista los servicios contratados de
// ese tipo y añade el bloque de contexto propio de cada uno.
function ProductoTab({ titulo, vacio, descripcionVacia, icono, servicios, extra, onManageProducts }) {
  return (
    <div className="space-y-4 animate-fade-in">
      <SectionTitle>{titulo}</SectionTitle>
      {servicios.length === 0
        ? (
          <div className="rounded-2xl border border-sky-100 bg-gradient-to-br from-white via-sky-50 to-navy-50 p-5 shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex max-w-xl gap-3">
                <div className="rounded-xl bg-navy p-3 text-white">{icono || <Package className="h-6 w-6" />}</div>
                <div>
                  <h3 className="font-extrabold text-navy-900">Aún no hay información operativa</h3>
                  <p className="mt-1 text-sm text-navy-900/70">{vacio}</p>
                  {descripcionVacia && <p className="mt-2 text-sm text-navy-900/55">{descripcionVacia}</p>}
                </div>
              </div>
              <Button
                variant="secondary"
                icon={<Package className="h-4 w-4" />}
                className="!bg-[#0B2460] !text-white hover:!bg-[#086CA8]"
                onClick={onManageProducts}
              >
                Ver productos
              </Button>
            </div>
          </div>
        )
        : (
          <div className="space-y-2">
            {servicios.map((service) => (
              <div key={service.assignment_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-navy-50 p-3">
                <div>
                  <p className="font-semibold text-navy-900">{service.name}</p>
                  <p className="text-xs text-navy-900/55">{service.description}</p>
                </div>
                <Badge tone={SERVICE_STATUS[service.service_status]?.tone || 'navy'}>
                  {SERVICE_STATUS[service.service_status]?.label || service.service_status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      {extra}
    </div>
  );
}
